import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Alert, ScrollView, ActivityIndicator, Image, ImageBackground, Linking, Modal, Platform } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import { TestUserManager } from '../utils/testUsers';
import { biometricAuthService } from '../services/biometricAuthService';
import { AuthManager } from '../utils/authManager';
import { UnifiedAuthService } from '../services/unifiedAuthService';
import { appointmentManager } from '../utils/appointmentManager';
import UserBottomMenu from '../components/UserBottomMenu';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import BookAPTLogo from '../components/BookAPTLogo';
import Svg, { Circle, Path, Rect } from 'react-native-svg';

const CORAL = '#e76f51';
const NAVY = '#1d3557';
const DARK_HEADER = '#1b2838';
const CREAM = '#faf8f5';
const CARD_BG = '#ffffff';
const TEXT_MUTED = '#64748b';
const BORDER = '#e8e4df';

const FaceIdIcon = ({ size = 22, color = NAVY }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M7 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"
      stroke={color}
      strokeWidth={1.6}
      strokeLinejoin="round"
    />
    <Circle cx={9.5} cy={10.5} r={1.1} fill={color} />
    <Circle cx={14.5} cy={10.5} r={1.1} fill={color} />
    <Path
      d="M10.2 14.2c.9.9 2.7.9 3.6 0"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
    />
  </Svg>
);

const WebBusinessChartIcon = () => (
  <Svg width={26} height={26} viewBox="0 0 24 24" fill="none">
    <Rect x="3" y="13" width="4.5" height="8" rx="1.2" fill={CORAL} />
    <Rect x="9.75" y="8" width="4.5" height="13" rx="1.2" fill={CORAL} />
    <Rect x="16.5" y="4" width="4.5" height="17" rx="1.2" fill={CORAL} />
  </Svg>
);

const WebSmsIcon = () => (
  <Svg width={26} height={26} viewBox="0 0 24 24" fill="none">
    <Path
      d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
      stroke={CORAL}
      strokeWidth={1.8}
      strokeLinejoin="round"
    />
  </Svg>
);

interface LoginScreenProps {
  onLoginSuccess: (loginType?: 'customer' | 'business-owner' | 'super-admin') => void;
  onCreateAccount?: () => void;
  onHelp?: () => void;
}

const WEB_LOGIN_FEATURES = [
  {
    iconKind: 'bookapt' as const,
    title: 'Book appointments',
    description: 'Easily schedule and manage appointments in one place.',
  },
  {
    iconKind: 'business' as const,
    title: 'Manage business',
    description: 'Streamline operations and keep your schedule organized.',
  },
  {
    iconKind: 'sms' as const,
    title: 'SMS reminders',
    description: 'Reduce no-shows with automated SMS reminders.',
  },
];

const LoginScreen: React.FC<LoginScreenProps> = ({ onLoginSuccess, onCreateAccount, onHelp }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [stayLoggedIn, setStayLoggedIn] = useState(true);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [biometricStatus, setBiometricStatus] = useState({
    isAvailable: false,
    isEnabled: false,
    biometryType: '',
    canUseBiometric: false
  });
  const [isCheckingBiometric, setIsCheckingBiometric] = useState(true);
  const [, setLogoTapCount] = useState(0);
  const [, setLastLogoTapAt] = useState(0);
  const logoTapCountRef = useRef(0);
  const lastLogoTapAtRef = useRef(0);
  const [showSuperAdminPinModal, setShowSuperAdminPinModal] = useState(false);
  const [superAdminPin, setSuperAdminPin] = useState('');
  const [isWideWeb, setIsWideWeb] = useState(true);

  useEffect(() => {
    checkBiometricStatus();
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const updateLayout = () => setIsWideWeb(window.innerWidth >= 900);
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  const checkBiometricStatus = async () => {
    try {
      const status = await biometricAuthService.getBiometricStatus();
      setBiometricStatus(status);
    } catch (error) {
      console.error('Error checking biometric status:', error);
    } finally {
      setIsCheckingBiometric(false);
    }
  };

  const handleBiometricLogin = async () => {
    if (!biometricStatus.canUseBiometric) {
      Alert.alert('Biometric Unavailable', 'Biometric authentication is not available or enabled.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await biometricAuthService.authenticateWithBiometric();
      if (result.success) {
        // Get stored credentials
        const credentials = await biometricAuthService.getBiometricCredentials();
        if (credentials) {
          // Validate credentials
          const isValid = await biometricAuthService.validateBiometricCredentials(credentials.username, credentials.password);
          if (isValid) {
            onLoginSuccess('customer');
          } else {
            Alert.alert('Authentication Failed', 'Stored credentials are invalid. Please login manually.');
          }
        } else {
          Alert.alert('No Credentials', 'No biometric credentials found. Please login manually first.');
        }
      } else {
        Alert.alert('Biometric Failed', result.error || 'Biometric authentication failed. Please try again.');
      }
    } catch (error) {
      console.error('Error with biometric login:', error);
      Alert.alert('Error', 'Biometric authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    setIsLoading(true);

    // Simulate account creation
    setTimeout(() => {
      onLoginSuccess('customer');
      setIsLoading(false);
    }, 1500);
  };

  const handleTestUserLogin = async () => {
    console.log('🚀🚀🚀🚀🚀 handleTestUserLogin CALLED 🚀🚀🚀🚀🚀');
    console.log('🚀 Email:', email);
    console.log('🚀 Password provided:', password ? 'YES' : 'NO');
    
    if (!email || !password) {
      Alert.alert('Error', 'Please enter both email and password');
      return;
    }

    console.log('🔍 Attempting login with:', { email, password });
    setIsLoading(true);
    try {
      console.log('🚀 LOGIN STARTED - Email:', email, 'Password length:', password.length);
      let success = false;
      let loginType: 'customer' | 'business-owner' = 'customer';
      let firebaseError: string | null = null; // Store Firebase error for user feedback
      
      // Try Firebase Auth login first (actual users who created accounts)
      console.log('═══════════════════════════════════════════════════════════');
      console.log('🔍 STEP 1: Trying Firebase Auth login for:', email);
      console.log('═══════════════════════════════════════════════════════════');
      
      try {
        const firebaseUser = await UnifiedAuthService.loginUser(email, password);
        console.log('🔍 STEP 1 RESULT: Firebase Auth login result:', firebaseUser ? 'SUCCESS ✅' : 'FAILED ❌');
        if (firebaseUser) {
          success = true;
          const ownerType = firebaseUser.userType;
          loginType =
            ownerType === 'business_owner' || ownerType === 'business'
              ? 'business-owner'
              : 'customer';
          console.log('✅✅✅ Firebase Auth login successful:', firebaseUser.email, 'type:', loginType);
        } else {
          console.log('❌❌❌ Firebase Auth login failed - user not found or credentials incorrect');
        }
      } catch (error: any) {
        console.log('❌❌❌ Firebase Auth login failed:', error.message);
        console.log('❌❌❌ Error code:', error.code);
        
        // Store Firebase error for better user feedback
        if (error.code === 'auth/user-not-found') {
          firebaseError = 'No account found with this email. Please register first or check if you completed the password reset.';
        } else if (error.code === 'auth/wrong-password') {
          firebaseError = 'Incorrect password. If you reset your password, make sure you completed the reset flow and are using the NEW password.';
        } else if (error.code === 'auth/invalid-email') {
          firebaseError = 'Invalid email address. Please check and try again.';
        } else if (error.code === 'auth/user-disabled') {
          firebaseError = 'This account has been disabled. Please contact support.';
        }
        // Continue to try other login methods (test users, business owners)
      }
      
      // Fallback: Try old localStorage-based login (for backward compatibility)
      if (!success) {
        console.log('🔍 STEP 1B: Trying localStorage-based login (backward compatibility)...');
        try {
          const registeredUser = await AuthManager.loginUser(email, password);
          if (registeredUser) {
            success = true;
            loginType = 'customer';
            console.log('✅✅✅ localStorage login successful (backward compatibility):', registeredUser.email);
          }
        } catch (error) {
          console.log('❌ localStorage login also failed');
        }
      }
      
      // If not found, try test users
      if (!success) {
        console.log('🔍 Trying test user login...');
        success = await TestUserManager.loginTestUser(email, password);
        console.log('🔍 Test user login result:', success);
        if (success) {
          loginType = 'customer';
        }
      }
      
      // If still not found, try business owner login
      if (!success) {
        console.log('🔍 Trying business owner login...');
        const businessOwner = await AuthManager.loginBusinessOwner(email, password);
        success = businessOwner !== null;
        console.log('🔍 Business owner login result:', success, businessOwner?.companyName);
        
        if (success) {
          loginType = 'business-owner';
          console.log('✅ Setting login type to business-owner');
        }
      }
      
      if (success) {
        // Enable biometric authentication silently for future logins (no popup)
        if (biometricStatus.isAvailable && !biometricStatus.isEnabled) {
          try {
            await biometricAuthService.enableBiometricAuth(
              email,
              password,
              biometricStatus.biometryType || 'TouchID'
            );
            console.log('✅ Biometric authentication enabled silently for future logins');
          } catch (error) {
            console.error('Error enabling biometric auth:', error);
          }
        }
        console.log('🔍 Calling onLoginSuccess with type:', loginType);
        onLoginSuccess(loginType);
      } else {
        // Show specific error message if we have one from Firebase
        const errorMessage = firebaseError || 
          'Invalid email or password. If you reset your password, make sure you completed the reset flow and are using the NEW password.';
        
        Alert.alert(
          'Login Failed', 
          errorMessage,
          [
            { text: 'OK', style: 'default' },
            { 
              text: 'Forgot Password', 
              onPress: () => setShowForgotPasswordModal(true),
              style: 'default'
            }
          ]
        );
      }
    } catch (error) {
      console.error('Login error:', error);
      Alert.alert('Error', 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickLogin = async (testEmail: string, testPassword: string) => {
    setEmail(testEmail);
    setPassword(testPassword);
    setShowLoginForm(true);
    
    // Automatically trigger login after setting credentials
    setTimeout(async () => {
      await handleTestUserLogin();
    }, 100);
  };

  const handleLogoTap = () => {
    const now = Date.now();
    const maxTapGapMs = 2000;
    const requiredTapCount = 6;
    const nextCount = now - lastLogoTapAtRef.current <= maxTapGapMs
      ? logoTapCountRef.current + 1
      : 1;

    logoTapCountRef.current = nextCount;
    lastLogoTapAtRef.current = now;
    setLogoTapCount(nextCount);
    setLastLogoTapAt(now);

    if (nextCount >= requiredTapCount) {
      logoTapCountRef.current = 0;
      lastLogoTapAtRef.current = 0;
      setLogoTapCount(0);
      setLastLogoTapAt(0);
      setSuperAdminPin('');
      setShowSuperAdminPinModal(true);
    }
  };

  const handleSuperAdminPinSubmit = async () => {
    if (superAdminPin !== '4490') {
      Alert.alert('Invalid PIN', 'Incorrect super admin PIN.');
      setSuperAdminPin('');
      return;
    }

    setIsLoading(true);
    try {
      const currentUser = await UnifiedAuthService.getCurrentUser();
      if (!currentUser) {
        const adminEmail =
          (email && email.trim()) ||
          (typeof process !== 'undefined' ? String(process.env.REACT_APP_SUPER_ADMIN_EMAIL || '').trim() : '');
        const adminPassword =
          password ||
          (typeof process !== 'undefined' ? String(process.env.REACT_APP_SUPER_ADMIN_PASSWORD || '').trim() : '');

        if (adminEmail && adminPassword) {
          try {
            await UnifiedAuthService.loginUser(adminEmail, adminPassword);
          } catch {
            // Admin API key may still load dashboard without Firebase auth.
          }
        }
      }

      setShowSuperAdminPinModal(false);
      setSuperAdminPin('');
      onLoginSuccess('super-admin');
    } catch {
      setShowSuperAdminPinModal(false);
      setSuperAdminPin('');
      onLoginSuccess('super-admin');
    } finally {
      setIsLoading(false);
    }
  };

  const showFaceIdLogin =
    Platform.OS !== 'web' &&
    !isCheckingBiometric &&
    biometricStatus.canUseBiometric &&
    (biometricStatus.biometryType === 'FaceID' || biometricStatus.biometryType === 'Face');

  const openWebLink = (path: string) => {
    const url = path.startsWith('http') ? path : `${typeof window !== 'undefined' ? window.location.origin : ''}${path}`;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      Linking.openURL(url).catch(() => undefined);
    }
  };

  const renderWebFeatureIcon = (iconKind: 'bookapt' | 'business' | 'sms') => {
    switch (iconKind) {
      case 'bookapt':
        return (
          <BookAPTLogo
            size="sm"
            imageStyle={styles.webFeatureLogoImage}
            style={styles.webFeatureLogoWrap}
          />
        );
      case 'business':
        return <WebBusinessChartIcon />;
      case 'sms':
        return <WebSmsIcon />;
      default:
        return null;
    }
  };

  const renderBrandPanel = () => (
    <View style={[styles.webBrandPanel, !isWideWeb && styles.webBrandPanelStacked]}>
      <ImageBackground
        source={{ uri: '/hero-bg.jpg' }}
        style={styles.webBrandBg}
        imageStyle={styles.webBrandBgImage}
      >
        <View style={styles.webBrandOverlay}>
          <BookAPTLogo
            size="lg"
            onPress={handleLogoTap}
            style={styles.webBrandLogoCard}
          />
          <Text style={styles.webBrandTitle}>BookAPT</Text>
          <Text style={styles.webBrandHeadline}>Welcome back</Text>
          <Text style={styles.webBrandSub}>
            One app for customers, business owners, and care coordination.
          </Text>
          <View style={styles.webFeatureList}>
            {WEB_LOGIN_FEATURES.map((feature) => (
              <View key={feature.title} style={styles.webFeatureRow}>
                <View style={styles.webFeatureIconCircle}>
                  {renderWebFeatureIcon(feature.iconKind)}
                </View>
                <View style={styles.webFeatureCopy}>
                  <Text style={styles.webFeatureTitle}>{feature.title}</Text>
                  <Text style={styles.webFeatureDesc}>{feature.description}</Text>
                </View>
              </View>
            ))}
          </View>
          <TouchableOpacity onPress={() => openWebLink('/')} style={styles.webBackHome}>
            <Text style={styles.webBackHomeText}>← Back to home</Text>
          </TouchableOpacity>
        </View>
      </ImageBackground>
    </View>
  );

  const renderLoginForm = () => (
    <>
      {Platform.OS === 'web' && (
      <View style={styles.logoSectionWeb}>
        {!isWideWeb && (
          <BookAPTLogo
            size="md"
            onPress={handleLogoTap}
            style={styles.webFormLogoCard}
          />
        )}
        <Text style={[styles.formTitle, Platform.OS === 'web' && styles.formTitleWeb]}>
          {Platform.OS === 'web' ? 'Sign in to BookAPT' : 'Welcome back'}
        </Text>
        <Text style={styles.formSubtitleWeb}>
          Sign in to book and manage your appointments.
        </Text>
      </View>
      )}

      <View
        style={[
          styles.loginFormSection,
          Platform.OS === 'web' && styles.loginFormSectionWeb,
          Platform.OS !== 'web' && styles.loginFormCard,
        ]}
      >
        {Platform.OS !== 'web' && (
          <View style={styles.mobileCardLogoRow}>
            <BookAPTLogo size="sm" imageStyle={styles.mobileCardLogoImage} />
            <Text style={styles.formTitleMobile}>Sign in</Text>
          </View>
        )}
        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>Email address</Text>
          <AppTextInput
            style={[styles.modernInput, Platform.OS === 'web' && styles.modernInputWeb]}
            value={email}
            onChangeText={setEmail}
            placeholder="Enter your email"
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
            textContentType="emailAddress"
          />
        </View>

        <View style={styles.inputContainer}>
          <View style={styles.passwordHeader}>
            <Text style={styles.inputLabel}>Password</Text>
            <TouchableOpacity onPress={() => setShowForgotPasswordModal(true)}>
              <Text style={[styles.forgotText, Platform.OS === 'web' && styles.forgotTextWeb]}>Forgot?</Text>
            </TouchableOpacity>
          </View>
          <View style={[styles.passwordInputContainer, Platform.OS === 'web' && styles.passwordInputContainerWeb]}>
            <AppTextInput
              style={styles.passwordInput}
              value={password}
              onChangeText={setPassword}
              placeholder="Enter your password"
              secureTextEntry={!showPassword}
              autoComplete="current-password"
              textContentType="password"
            />
            <TouchableOpacity
              style={styles.eyeIconButton}
              onPress={() => setShowPassword(!showPassword)}
            >
              <Text style={styles.eyeIcon}>{showPassword ? '👁️' : '👁️‍🗨️'}</Text>
            </TouchableOpacity>
          </View>
        </View>

        {Platform.OS !== 'web' && (
          <TouchableOpacity
            style={styles.checkboxContainer}
            onPress={() => setStayLoggedIn(!stayLoggedIn)}
            activeOpacity={0.8}
          >
            <View style={[styles.checkbox, stayLoggedIn && styles.checkboxChecked]}>
              {stayLoggedIn && <Text style={styles.checkmark}>✓</Text>}
            </View>
            <Text style={styles.checkboxLabel}>Stay signed in</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={[
            styles.loginButton,
            Platform.OS === 'web' ? styles.loginButtonWeb : styles.loginButtonMobile,
            isLoading && styles.loginButtonDisabled,
          ]}
          onPress={handleTestUserLogin}
          disabled={isLoading}
        >
          {isLoading ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.loginButtonText}>Sign in</Text>
          )}
        </TouchableOpacity>

        {showFaceIdLogin && (
          <>
            <View style={styles.faceIdDividerRow}>
              <View style={styles.faceIdDividerLine} />
              <Text style={styles.faceIdDividerText}>or</Text>
              <View style={styles.faceIdDividerLine} />
            </View>
            <TouchableOpacity
              style={[styles.faceIdButton, isLoading && styles.faceIdButtonDisabled]}
              onPress={handleBiometricLogin}
              disabled={isLoading}
              activeOpacity={0.85}
            >
              <View style={styles.faceIdIconWrap}>
                <FaceIdIcon size={24} color={NAVY} />
              </View>
              <Text style={styles.faceIdButtonText}>Sign in with Face ID</Text>
            </TouchableOpacity>
          </>
        )}

        <View style={styles.signUpContainer}>
          <Text style={styles.signUpText}>Don&apos;t have an account? </Text>
          <TouchableOpacity onPress={onCreateAccount}>
            <Text style={[styles.signUpLink, Platform.OS === 'web' && styles.signUpLinkWeb]}>Create account</Text>
          </TouchableOpacity>
        </View>

        {Platform.OS === 'web' && (
          <View style={styles.webLegalLinks}>
            <TouchableOpacity onPress={() => openWebLink('/privacy.html')}>
              <Text style={styles.webLegalLinkText}>Privacy Policy</Text>
            </TouchableOpacity>
            <Text style={styles.webLegalDivider}>·</Text>
            <TouchableOpacity onPress={() => openWebLink('/terms.html')}>
              <Text style={styles.webLegalLinkText}>Terms of Service</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {Platform.OS !== 'web' && (
        <View style={styles.poweredByContainer}>
          <TouchableOpacity
            onPress={() => Linking.openURL('https://orynsolutions.io')}
            activeOpacity={0.7}
          >
            <Text style={styles.poweredByText}>Powered by Oryn Inc.</Text>
          </TouchableOpacity>
        </View>
      )}
    </>
  );

  const isWebSplit = Platform.OS === 'web';

  return (
    <View style={[styles.mainContainer, isWebSplit && styles.mainContainerWeb]}>
      {isWebSplit ? (
        <View style={[styles.webSplitRow, !isWideWeb && styles.webSplitColumn]}>
          {renderBrandPanel()}
          <ScrollView
            style={styles.webFormPanel}
            contentContainerStyle={styles.webFormPanelContent}
          >
            {renderLoginForm()}
          </ScrollView>
        </View>
      ) : (
        <ScrollView
          style={styles.containerMobile}
          contentContainerStyle={styles.scrollContentMobileDark}
          keyboardShouldPersistTaps="handled"
          bounces={false}
        >
          <View style={styles.mobileDarkHeader}>
            <BookAPTLogo
              size="header"
              onPress={handleLogoTap}
              style={styles.mobileHeaderLogoCard}
            />
            <Text style={styles.appTitleHeader}>BookAPT</Text>
            <Text style={styles.taglineHeader}>One app for all your appointments</Text>
          </View>
          <View style={styles.mobileFormArea}>
            {renderLoginForm()}
          </View>
        </ScrollView>
      )}

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        visible={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />

      <Modal
        visible={showSuperAdminPinModal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setShowSuperAdminPinModal(false);
          setSuperAdminPin('');
        }}
      >
        <View style={styles.pinModalOverlay}>
          <View style={styles.pinModalCard}>
            <Text style={styles.pinModalTitle}>Super Admin PIN</Text>
            <Text style={styles.pinModalSubtitle}>Enter PIN to continue</Text>
            <AppTextInput
              style={styles.pinInput}
              value={superAdminPin}
              onChangeText={(text) => setSuperAdminPin(text.replace(/[^0-9]/g, '').slice(0, 6))}
              placeholder="Enter PIN"
              keyboardType="number-pad"
              secureTextEntry
              maxLength={6}
              autoFocus
            />
            <View style={styles.pinModalActions}>
              <TouchableOpacity
                style={[styles.pinButton, styles.pinCancelButton]}
                onPress={() => {
                  setShowSuperAdminPinModal(false);
                  setSuperAdminPin('');
                }}
              >
                <Text style={styles.pinCancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.pinButton, styles.pinSubmitButton]}
                onPress={handleSuperAdminPinSubmit}
              >
                <Text style={styles.pinSubmitButtonText}>Unlock</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: Platform.OS === 'web' ? CREAM : CARD_BG,
    minHeight: '100vh',
  },
  container: {
    flex: 1,
    backgroundColor: CREAM,
  },
  containerMobile: {
    flex: 1,
    backgroundColor: CARD_BG,
  },
  scrollContent: {
    paddingBottom: 100,
    flexGrow: 1,
  },
  scrollContentMobileDark: {
    flexGrow: 1,
    paddingBottom: 32,
  },
  mobileDarkHeader: {
    backgroundColor: DARK_HEADER,
    paddingTop: Platform.OS === 'ios' ? 56 : 44,
    paddingBottom: 40,
    paddingHorizontal: 28,
    alignItems: 'center',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  mobileHeaderLogoCard: {
    marginBottom: 16,
  },
  webBrandLogoCard: {
    marginBottom: 16,
  },
  webFormLogoCard: {
    marginBottom: 16,
    alignSelf: 'center',
  },
  webFeatureLogoWrap: {
    width: 34,
    height: 34,
  },
  webFeatureLogoImage: {
    width: 34,
    height: 34,
  },
  appTitleHeader: {
    fontSize: 28,
    fontWeight: '800',
    color: '#ffffff',
    marginBottom: 8,
    letterSpacing: -0.3,
  },
  taglineHeader: {
    fontSize: 15,
    color: 'rgba(255, 255, 255, 0.78)',
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 280,
  },
  mobileFormArea: {
    flex: 1,
    backgroundColor: CARD_BG,
    paddingHorizontal: 20,
    marginTop: -20,
    paddingTop: 4,
  },
  mobileCardLogoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 18,
    width: '100%',
    gap: 12,
  },
  mobileCardLogoImage: {
    width: 40,
    height: 40,
  },
  formTitleMobile: {
    fontSize: 22,
    fontWeight: '800',
    color: NAVY,
    textAlign: 'left',
    flex: 1,
  },
  loginFormCard: {
    backgroundColor: CARD_BG,
    borderRadius: 20,
    paddingHorizontal: 20,
    paddingTop: 28,
    paddingBottom: 12,
    marginBottom: 12,
    width: '100%',
    maxWidth: 440,
    alignSelf: 'center',
    borderWidth: 1,
    borderColor: BORDER,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.1,
    shadowRadius: 24,
    elevation: 8,
  },
  loginButtonMobile: {
    backgroundColor: CORAL,
    shadowColor: CORAL,
  },
  faceIdDividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
    marginTop: 4,
    width: '100%',
    maxWidth: 400,
  },
  faceIdDividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: BORDER,
  },
  faceIdDividerText: {
    marginHorizontal: 12,
    fontSize: 13,
    color: TEXT_MUTED,
    fontWeight: '500',
  },
  faceIdButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: BORDER,
    backgroundColor: CARD_BG,
    marginBottom: 20,
    width: '100%',
    maxWidth: 400,
  },
  faceIdButtonDisabled: {
    opacity: 0.5,
  },
  faceIdIconWrap: {
    marginRight: 10,
  },
  faceIdButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: NAVY,
  },
  logoSection: {
    alignItems: 'center',
    paddingTop: 80,
    paddingBottom: 50,
    backgroundColor: CARD_BG,
  },
  logo: {
    fontSize: 80,
    marginBottom: 20,
  },
  professionalIcon: {
    width: 120,
    height: 120,
    marginBottom: 20,
  },
  iconBackground: {
    width: '100%',
    height: '100%',
    backgroundColor: '#4A90E2',
    borderRadius: 24,
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#4A90E2',
    shadowOffset: {
      width: 0,
      height: 8,
    },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 8,
  },
  calendarIcon: {
    fontSize: 60,
    color: 'rgba(255, 255, 255, 0.9)',
    position: 'absolute',
  },
  speechBubble: {
    fontSize: 36,
    color: 'rgba(255, 255, 255, 1)',
    position: 'absolute',
    top: 15,
    right: 15,
  },
  title: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 18,
    color: '#7f8c8d',
    textAlign: 'center',
  },
  chatSection: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 30,
  },
  chatBox: {
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
    width: '100%',
    maxWidth: 350,
  },
  chatText: {
    fontSize: 16,
    color: '#2c3e50',
    textAlign: 'center',
    lineHeight: 24,
  },
  buttonSection: {
    paddingHorizontal: 30,
    paddingBottom: 50,
  },
  createAccountText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    lineHeight: 20,
  },
  createAccountLink: {
    color: '#007AFF',
    fontWeight: 'bold',
    textDecorationLine: 'underline',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 8,
    textAlign: 'center',
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#7f8c8d',
    textAlign: 'center',
    marginBottom: 20,
  },
  loginFormSection: {
    paddingHorizontal: 24,
    paddingTop: 30,
    marginBottom: 20,
    alignItems: 'center',
    width: '100%',
    maxWidth: 450,
    alignSelf: 'center',
  },
  inputContainer: {
    marginBottom: 16,
    width: '100%',
    maxWidth: 400,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E5E5E7',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#2c3e50',
    backgroundColor: 'white',
  },
  loginButton: {
    backgroundColor: CORAL,
    paddingVertical: 16,
    borderRadius: 12,
    marginBottom: 16,
    shadowColor: CORAL,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.28,
    shadowRadius: 8,
    elevation: 4,
    width: '100%',
    maxWidth: 400,
  },
  loginButtonDisabled: {
    backgroundColor: '#bdc3c7',
    shadowOpacity: 0,
  },
  loginButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  // Biometric styles
  biometricSection: {
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  biometricButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#007AFF',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  biometricButtonDisabled: {
    backgroundColor: '#bdc3c7',
    shadowOpacity: 0,
    elevation: 0,
  },
  biometricIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  biometricButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  logoContainer: {
    width: 80,
    height: 80,
    borderRadius: 20,
    backgroundColor: '#fff5f2',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
    shadowColor: CORAL,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 5,
    borderWidth: 1,
    borderColor: BORDER,
  },
  logoImage: {
    width: 60,
    height: 60,
  },
  appTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  tagline: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  formTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 24,
    textAlign: 'center',
    width: '100%',
  },
  modernInput: {
    backgroundColor: '#F6F6F6',
    borderWidth: 1,
    borderColor: '#E5E5E7',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
    color: '#1C1C1E',
    width: '100%',
  },
  passwordHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  forgotText: {
    fontSize: 14,
    color: CORAL,
    fontWeight: '600',
  },
  passwordInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F6F6F6',
    borderWidth: 1,
    borderColor: '#E5E5E7',
    borderRadius: 12,
    paddingHorizontal: 16,
    width: '100%',
  },
  passwordInput: {
    flex: 1,
    paddingVertical: 14,
    fontSize: 16,
    color: '#1C1C1E',
  },
  eyeIconButton: {
    padding: 8,
  },
  eyeIcon: {
    fontSize: 20,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#C7C7CC',
    borderRadius: 4,
    marginRight: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: CORAL,
    borderColor: CORAL,
  },
  checkmark: {
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    fontSize: 14,
    color: '#1C1C1E',
  },
  signUpContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  signUpText: {
    fontSize: 14,
    color: '#666',
  },
  signUpLink: {
    fontSize: 14,
    color: CORAL,
    fontWeight: 'bold',
    textDecorationLine: 'underline',
  },
  poweredByContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
    paddingBottom: 30,
    marginTop: 20,
  },
  poweredByText: {
    fontSize: 12,
    color: '#999',
    fontWeight: 'bold',
    textDecorationLine: 'underline',
  },
  pinModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  pinModalCard: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 20,
  },
  pinModalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 6,
  },
  pinModalSubtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 14,
  },
  pinInput: {
    backgroundColor: '#F6F6F6',
    borderWidth: 1,
    borderColor: '#E5E5E7',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: '#1C1C1E',
    marginBottom: 16,
  },
  pinModalActions: {
    flexDirection: 'row',
    gap: 10,
  },
  pinButton: {
    flex: 1,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center',
  },
  pinCancelButton: {
    backgroundColor: '#E9ECEF',
  },
  pinSubmitButton: {
    backgroundColor: CORAL,
  },
  pinCancelButtonText: {
    color: '#1C1C1E',
    fontWeight: '600',
    fontSize: 15,
  },
  pinSubmitButtonText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 15,
  },
  mainContainerWeb: {
    backgroundColor: '#ffffff',
    minHeight: '100vh',
  },
  webSplitRow: {
    flex: 1,
    flexDirection: 'row',
    minHeight: '100vh',
    width: '100%',
  },
  webSplitColumn: {
    flexDirection: 'column',
  },
  webBrandPanel: {
    flex: 1,
    minHeight: 320,
    maxWidth: '50%',
  },
  webBrandPanelStacked: {
    maxWidth: '100%',
    minHeight: 280,
  },
  webBrandBg: {
    flex: 1,
    width: '100%',
    minHeight: '100%',
  },
  webBrandBgImage: {
    resizeMode: 'cover',
  },
  webBrandOverlay: {
    flex: 1,
    backgroundColor: 'rgba(27, 40, 56, 0.9)',
    paddingHorizontal: 48,
    paddingVertical: 56,
    justifyContent: 'center',
  },
  webBrandTitle: {
    fontSize: 28,
    fontWeight: '800',
    color: '#ffffff',
    marginBottom: 8,
  },
  webBrandHeadline: {
    fontSize: 34,
    fontWeight: '800',
    color: '#ffffff',
    marginBottom: 12,
    lineHeight: 40,
    maxWidth: 420,
  },
  webBrandSub: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.88)',
    lineHeight: 24,
    marginBottom: 28,
    maxWidth: 420,
  },
  webFeatureList: {
    gap: 28,
    marginBottom: 32,
    maxWidth: 440,
  },
  webFeatureRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  webFeatureIconCircle: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    flexShrink: 0,
  },
  webFeatureCopy: {
    flex: 1,
    paddingTop: 2,
  },
  webFeatureTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 6,
    lineHeight: 24,
  },
  webFeatureDesc: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.82)',
    lineHeight: 21,
  },
  webBackHome: {
    alignSelf: 'flex-start',
  },
  webBackHomeText: {
    color: '#e76f51',
    fontSize: 14,
    fontWeight: '600',
  },
  webFormPanel: {
    flex: 1,
    backgroundColor: '#ffffff',
    minWidth: 360,
  },
  webFormPanelContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: 48,
    paddingHorizontal: 24,
  },
  logoSectionWeb: {
    alignItems: 'center',
    marginBottom: 8,
    width: '100%',
    maxWidth: 420,
    alignSelf: 'center',
  },
  formTitleWeb: {
    fontSize: 28,
    textAlign: 'left',
    alignSelf: 'flex-start',
    width: '100%',
    color: '#1d3557',
  },
  formSubtitleWeb: {
    fontSize: 15,
    color: '#64748b',
    marginBottom: 28,
    textAlign: 'left',
    alignSelf: 'flex-start',
    width: '100%',
  },
  loginFormSectionWeb: {
    maxWidth: 420,
    paddingHorizontal: 0,
    paddingTop: 0,
    alignItems: 'stretch',
  },
  modernInputWeb: {
    backgroundColor: '#ffffff',
  },
  passwordInputContainerWeb: {
    backgroundColor: '#ffffff',
  },
  loginButtonWeb: {
    backgroundColor: '#e76f51',
    shadowColor: '#e76f51',
  },
  forgotTextWeb: {
    color: '#e76f51',
  },
  signUpLinkWeb: {
    color: '#e76f51',
  },
  webLegalLinks: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
    gap: 8,
  },
  webLegalLinkText: {
    fontSize: 13,
    color: '#64748b',
    textDecorationLine: 'underline',
  },
  webLegalDivider: {
    color: '#94a3b8',
    fontSize: 13,
  },
});

export default LoginScreen;